import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Space, Spin, Tag, Typography, Upload, notification,
} from 'antd';
import { CheckOutlined, LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';

const { Text, Paragraph } = Typography;

const evidenceLabel = (mode) => {
  if (mode === 'required') return <Tag color="red">Required</Tag>;
  if (mode === 'optional') return <Tag color="blue">Optional</Tag>;
  return <Tag>Not needed</Tag>;
};

const PaEvidenceUploadPanel = ({ checkinId, onComplete, onCancel }) => {
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingLineId, setUploadingLineId] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState(null);

  const loadPolicy = useCallback(async () => {
    if (!checkinId) return;
    setLoading(true);
    setError(null);
    try {
      const parsed = lib.parseApiEnvelope(await lib.getPaEvidencePolicy(checkinId));
      if (!parsed.ok) {
        setError(parsed.message || 'Unable to load evidence requirements');
        setPolicy(null);
        return;
      }
      setPolicy(parsed.data ?? parsed.body);
    } catch (e) {
      setError(e?.message || 'Unable to load evidence requirements');
      setPolicy(null);
    } finally {
      setLoading(false);
    }
  }, [checkinId]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const workflow = policy?.workflow ?? policy ?? {};
  const headerMode = workflow.pa_header_evidence_mode ?? policy?.pa_header_evidence_mode ?? 'not_needed';
  const headerFiles = policy?.header_files ?? policy?.header_attachments ?? [];
  const lineRules = policy?.line_rules ?? policy?.items ?? policy?.item_rules ?? [];

  const uploadHeader = async (file) => {
    setUploadingHeader(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.uploadPaHeaderEvidence(checkinId, file));
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Header upload failed' });
        return;
      }
      notification.success({ message: parsed.message || 'Header document uploaded' });
      await loadPolicy();
    } catch (e) {
      notification.error({ message: e?.message || 'Header upload failed' });
    } finally {
      setUploadingHeader(false);
    }
  };

  const uploadLine = async (claimItemId, file) => {
    setUploadingLineId(claimItemId);
    try {
      const parsed = lib.parseApiEnvelope(await lib.uploadPaItemEvidence(checkinId, claimItemId, file));
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Line upload failed' });
        return;
      }
      notification.success({ message: parsed.message || 'Line document uploaded' });
      await loadPolicy();
    } catch (e) {
      notification.error({ message: e?.message || 'Line upload failed' });
    } finally {
      setUploadingLineId(null);
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.finalizePaWithEvidence(checkinId));
      if (!parsed.ok) {
        if (parsed.body?.code === 'PA_EVIDENCE_REQUIRED') {
          notification.warning({
            message: parsed.message || 'Required documents are still missing',
            duration: 6,
          });
          await loadPolicy();
          return;
        }
        notification.error({ message: parsed.message || 'Finalize failed' });
        return;
      }
      notification.success({ message: parsed.message || 'PA finalized' });
      onComplete?.(parsed.data ?? parsed.body);
    } catch (e) {
      const code = e?.response?.data?.code;
      const message = e?.response?.data?.message || e?.message || 'Finalize failed';
      if (code === 'PA_EVIDENCE_REQUIRED') {
        notification.warning({ message, duration: 6 });
        await loadPolicy();
      } else {
        notification.error({ message });
      }
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin indicator={<LoadingOutlined spin />} />
        <Paragraph type="secondary" style={{ marginTop: 12 }}>Loading evidence requirements…</Paragraph>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={error}
        action={(
          <Space>
            <Button size="small" onClick={loadPolicy}>Retry</Button>
            {onCancel && <Button size="small" onClick={onCancel}>Close</Button>}
          </Space>
        )}
      />
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="Next steps — your PA lines are already saved"
        description={(
          <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            <li>Upload any <strong>required</strong> documents below (referral letter, lab report, etc.).</li>
            <li>Click <strong>Finalize PA</strong> — the system checks evidence and may auto-approve lines if configured.</li>
            <li>You can close this dialog and return later; the encounter keeps your lines until you finalize.</li>
          </ol>
        )}
      />

      <Card size="small" title="Step 1 — PA header document (whole encounter)">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            Requirement: {evidenceLabel(headerMode)}
          </div>
          {headerFiles?.length > 0 && (
            <Text type="secondary">{headerFiles.length} file(s) already uploaded</Text>
          )}
          {headerMode !== 'not_needed' && (
            <Upload
              accept="image/*,.pdf,.doc,.docx"
              showUploadList={false}
              beforeUpload={(file) => {
                uploadHeader(file);
                return false;
              }}
            >
              <Button icon={<UploadOutlined />} loading={uploadingHeader}>
                Upload header document
              </Button>
            </Upload>
          )}
        </Space>
      </Card>

      {lineRules.length > 0 && (
        <Card size="small" title="Step 2 — Per-line documents">
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {lineRules.map((line) => {
              const claimItemId = line.claim_item_id ?? line.id ?? line.item_id;
              const mode = line.evidence_mode ?? line.required_evidence_mode ?? 'not_needed';
              const hasDoc = Boolean(line.has_doc ?? line.doc ?? line.attachment_url);
              if (mode === 'not_needed') return null;
              return (
                <div
                  key={claimItemId}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <div>
                    <Text strong>{line.item_name ?? line.label ?? `Line #${claimItemId}`}</Text>
                    <div>{evidenceLabel(mode)} {hasDoc && <CheckOutlined style={{ color: '#52c41a' }} />}</div>
                  </div>
                  <Upload
                    accept="image/*,.pdf,.doc,.docx"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      uploadLine(claimItemId, file);
                      return false;
                    }}
                  >
                    <Button
                      size="small"
                      icon={<UploadOutlined />}
                      loading={uploadingLineId === claimItemId}
                      disabled={!claimItemId}
                    >
                      Upload
                    </Button>
                  </Upload>
                </div>
              );
            })}
          </Space>
        </Card>
      )}

      <Space wrap>
        <Button type="primary" loading={finalizing} onClick={handleFinalize}>
          Step 3 — Finalize PA
        </Button>
        {onCancel && <Button onClick={onCancel}>Save for later (lines kept on encounter)</Button>}
      </Space>
    </Space>
  );
};

export default PaEvidenceUploadPanel;
